import { Company } from '@apis/Customers/model';
import { QueryExpr } from '@apis/Resources';
import { BaseResource, ReservedEc2 } from '@apis/Resources/model';
import { GridChartSettings } from '@root/Components/Charts/GridChart';
import { KpiChartSettings } from '@root/Components/Charts/KpiChart';
import { ChartConfig } from '@root/Components/DashboardLayout/Charts/ChartRenderer';
import { getInputSpec } from '@root/Components/Settings/InputSpec';
import { ICompanyContextToken } from '@root/Services/Customers/CompanyContext';
import { AppFeatureNames } from '@root/Services/Customers/CompanyFeatureService';
import { IUserFeatureAccessLookup, UserFeatureAccessService } from '@root/Services/Customers/UserFeatureAccessService';
import { EventEmitter } from '@root/Services/EventEmitter';
import { NamedFormats } from '@root/Services/FormatService';
import { QueryDatasource, QueryDatasourceMetadata } from '@root/Services/Query/QueryDatasource';
import { exprBuilder, SchemaService } from '@root/Services/QueryExpr';
import { IdleResourcesService } from '@root/Services/Resources/IdleResourcesService';
import { ResourceQueryDatasourceFactory } from '@root/Services/Resources/ResourceQueryDatasourceFactory';
import { ResourceQueryService } from '@root/Services/Resources/ResourceService';
import { BorderAll, CircleNumber1, Eye } from 'tabler-icons-react';
import { inject, injectable } from 'tsyringe';
import { describeDatasource } from '../../Components/DatasourceDescriptor';
import { BaseDatasourcePresetsBuilder, IDatasourcePresetsBuilderFactory, INotificationPresetItem } from './BasePresets';

class ResourceDatasourcePresetsBuilder extends BaseDatasourcePresetsBuilder {
    public constructor(
        queryDatasource: QueryDatasource,
        schemaSvc: SchemaService,
        private readonly featureAccessLookup: IUserFeatureAccessLookup,
        private readonly idleResourceQueries: Map<string, QueryExpr>
    ) {
        super(queryDatasource, schemaSvc);
    }

    public getPresets() {
        const { name, icon: Icon } = describeDatasource(this.baseQueryDatasource);
        return this.createDatasourcePresets(
            this.baseQueryDatasource.title ?? name,
            <Icon />,
            [
                ...this.createExpiringRiAlert(),
                this.createExpiringSpAlert(),
                ...this.createIdleResourceAlerts(),
                this.createTagHealthAlertPreset(),
                this.createCustomResourceAlertPreset(),
            ],
            [this.createWeeklyTagHealthReportPreset(), this.createCustomResourceReportPreset()]
        );
    }

    // #region Alerts
    private createExpiringRiAlert(): INotificationPresetItem<any>[] {
        const createByType = (
            resourceType: string,
            idSfx: string,
            typeLbl: string,
            idFld: string,
            stateFld: string,
            instanceTypeFld: string,
            instanceCtFld: string
        ) => {
            type RiParams = { pExpiration: Date; pDaysPrior: number; Region: string; End: Date; endDt: Date };

            const xb = this.createExprBuilder<RiParams & { end: Date }>();
            const [idExpr, instCtExpr, typeExpr, regExpr, endExpr] = xb.select({
                ID: xb.model[idFld],
                '# Instances': xb.sum(xb.model[instanceCtFld] as unknown as number),
                Type: xb.model[instanceTypeFld],
                Region: xb.model.Region,
                End: xb.model.End,
            });

            const id = this.createId('expiring-ri-' + idSfx);
            const initialized = this.createBehavior(({ data }) => {
                const result = this.mergeConfig(
                    {
                        Title: `${typeLbl} Reservations Expiring Soon`,
                        Description: `Some ${typeLbl} reservations will expire soon. Please review the details.`,
                        ComponentConfig: {
                            ...this.createCustomChartConfig('grid'),
                            settings: {
                                columns: [
                                    { id: 'id', select: idExpr, type: 'string', formatter: 'string' },
                                    { id: 'instCt', select: instCtExpr, type: 'number', formatter: 'int' },
                                    { id: 'type', select: typeExpr, type: 'string', formatter: 'string' },
                                    { id: 'region', select: regExpr, type: 'string', formatter: 'string' },
                                    { id: 'endDt', select: endExpr, type: 'date', formatter: 'short-date' },
                                ],
                                state: {
                                    columns: [
                                        { id: 'id', width: 250 },
                                        { id: 'instCt', width: 100 },
                                        { id: 'type', width: 140 },
                                        { id: 'region', width: 100 },
                                        { id: 'endDt', width: 110 },
                                    ],
                                    filters: [],
                                    sort: [{ Expr: xb.resolve(xb.model.End), Direction: 'Asc' }],
                                    groupBy: [],
                                },
                                allowFilter: false,
                            } as GridChartSettings,
                            filters: [xb.resolve(xb.model[stateFld].eq(['active'])), xb.resolve(xb.model.ResourceType!.eq([resourceType]))],
                        },
                        Trigger: this.defaultAlertTrigger('grid-alert', {
                            filter: [xb.resolve(xb.model.endDt.onOrBefore(xb.model.pExpiration))],
                        }),
                        PresentationOptions: { presetId: id },
                        QueryOptions: {
                            Parameters: {
                                pDaysPrior: { Value: 14 },
                                pExpiration: xb.resolve(xb.addDate(xb.currentDate(), xb.model.pDaysPrior, 'day')),
                            },
                            DatasourceName: this.baseQueryDatasource.name,
                            Filters: [],
                        },
                    },
                    data
                );
                return result;
            });

            const behavior = initialized.addForm(this.createStandardAlertBehavior()).addValidation(() => []);

            return {
                id,
                label: `Expiring ${typeLbl} Reservations`,
                description: `Notify in advance when ${typeLbl} reservations are near their expiration date.`,
                icon: <BorderAll />,
                behavior,
            };
        };

        return [
            createByType('EC2 Reserved', 'ec2', 'EC2', 'ReservedInstancesId', 'State.Value', 'InstanceType.Value', 'InstanceCount'),
            createByType('RDS Reserved', 'rds', 'RDS', 'ReservedDBInstanceArn', 'State', 'DBInstanceClass', 'DBInstanceCount'),
        ];
    }

    private createExpiringSpAlert(): INotificationPresetItem<any> {
        const xb = this.createExprBuilder<{
            'State.Value': string;
            SavingsPlanArn: string;
            End: Date;
            Commitment: string;
            TermDurationInSeconds: number;
            pExpiration: Date;
            pDaysPrior: number;
        }>();
        const id = this.createId('expiring-sp');
        const [sp, spType, expires, value] = this.selectExprs({
            'Savings Plan': xb.model.SavingsPlanArn,
            Type: xb.model['SavingsPlanType.Value'],
            Expiration: xb.model.End,
            Value: xb.sum(
                xb.convert<number>(xb.model.Commitment, 'number').times(xb.model.TermDurationInSeconds.dividedBy(60 * 60) as unknown as number)
            ),
        });

        const initialized = this.createBehavior(({ data }) => {
            const result = this.mergeConfig(
                {
                    Title: 'Savings Plan Expiration',
                    Description: 'Some savings plan will expire soon. Please review the details.',
                    ComponentConfig: {
                        ...this.createCustomChartConfig('grid'),
                        settings: {
                            columns: [
                                { id: 'spid', select: sp, type: 'string', formatter: 'string' },
                                { id: 'sptype', select: spType, type: 'string', formatter: 'string' },
                                { id: 'end', select: expires, type: 'date', formatter: 'short-date' },
                                { id: 'value', select: value, type: 'number', formatter: 'currency-2dec' },
                            ],
                            state: {
                                columns: [
                                    { id: 'spid', width: 250 },
                                    { id: 'sptype', width: 130 },
                                    { id: 'end', width: 110 },
                                    { id: 'value', width: 110 },
                                ],
                                filters: [],
                                sort: [{ Expr: xb.resolve(xb.model.End), Direction: 'Asc' }],
                                groupBy: [],
                            },
                            allowFilter: false,
                        } as GridChartSettings,
                    },
                    Trigger: {
                        TriggerType: 'Threshold',
                        Threshold: {
                            ThresholdCondition: xb.resolve(xb.and(xb.model.End.before(xb.model.pExpiration), xb.model['State.Value'].eq('active'))),
                            ThrottlePeriod: 'Daily',
                        },
                    },
                    PresentationOptions: { presetId: id },
                    QueryOptions: {
                        Parameters: {
                            pDaysPrior: { Value: 14 },
                            pExpiration: xb.resolve(xb.addDate(xb.currentDate(), xb.model.pDaysPrior, 'day')),
                        },
                        DatasourceName: this.baseQueryDatasource.name,
                        Filters: [],
                    },
                },
                data
            );
            return result;
        });

        const behavior = initialized.addForm(this.createStandardAlertBehavior()).addValidation(() => []);

        return {
            id,
            label: 'Expiring Savings Plans',
            description: 'Notify in advance when savings plans are near their expiration date.',
            icon: <BorderAll />,
            behavior,
        };
    }

    private createIdleResourceAlerts(): INotificationPresetItem<any>[] {
        const result: INotificationPresetItem<any>[] = [];
        if (this.featureAccessLookup.checkAccessByName(AppFeatureNames.IdleResources)) {
            const xb = this.createExprBuilder<BaseResource>();

            const resourceTypeIdleCrit = [
                { type: 'EC2', indexedType: ['EC2 On Demand'] },
                'RDS',
                'EMR',
                { type: 'ELB', indexedType: ['ELBV1', 'ELBV2'] },
                'Redshift',
            ]
                .map((rt) => (typeof rt === 'string' ? { type: rt, indexedType: [rt] } : rt))
                .map((rt) => ({ crit: this.idleResourceQueries.get(rt.type)!, ...rt }))
                .filter(({ crit }) => !!crit);
            for (const { type, indexedType, crit } of resourceTypeIdleCrit) {
                const id = this.createId('idle-' + type + '-count-alert');

                const idleExpr = xb.fromExpr<boolean>(crit);
                const [annCost, idle, all, idlePct] = xb.select({
                    annCost: xb.aggIf(idleExpr, xb.sum(xb.model.ExtendedAnnualizedCost!)),
                    idleCt: xb.count(idleExpr),
                    all: xb.count(),
                    idlePct: xb.op('percent', idleExpr),
                });
                const initialized = this.createBehavior(({ data }) => {
                    return this.mergeConfig(
                        {
                            Title: `Idle ${type} Alert`,
                            Description: `Alert threshold reached for idle ${type} resources.\nConsider terminating, stopping, or scheduling.`,
                            ComponentConfig: {
                                ...this.createCustomChartConfig('kpi'),
                                values: [annCost, idle, all, idlePct],
                                groups: [],
                                settings: {
                                    format: ['money-with-two-decimals', 'int', 'int', 'percent'],
                                    labels: [`Idle ${type} $/yr`, `# Idle ${type}`, `# ${type} Total`, `% Idle`],
                                },
                            },
                            Trigger: {
                                ...this.defaultAlertTrigger(),
                                Threshold: {
                                    PresentationOptions: data.Trigger?.PresentationOptions ?? {
                                        settings: [
                                            {
                                                optionId: 'kpi-alert',
                                                model: {
                                                    conditions: [
                                                        { left: 'idleCt', comparitor: 'gt', rightType: 'value', rightValue: 10 },
                                                        { left: 'annCost', comparitor: 'gte', rightType: 'value', rightValue: 10000 },
                                                    ],
                                                },
                                            },
                                        ],
                                    },
                                },
                            },
                            PresentationOptions: { presetId: id },
                            QueryOptions: {
                                Parameters: {},
                                DatasourceName: this.baseQueryDatasource.name,
                                Filters: [xb.resolve(xb.model.ResourceType!.eq(indexedType))],
                            },
                        },
                        data
                    );
                });

                const chartInput = this.createCustomChartBehavior(false);
                const behavior = initialized
                    .addForm(({ data }) => {
                        const configChanged = new EventEmitter(data.ComponentConfig!);
                        return [
                            { ...chartInput(data), collapsed: true },
                            this.createSection(
                                'Alert Settings',
                                this.createSectionItem('Title and Description', ...this.createDescriptionInputs(data)),
                                this.createAlertConditionSection(data, configChanged)
                            ),
                        ];
                    })
                    .addValidation(() => []);

                result.push({
                    id,
                    label: `Idle ${type} Above Threshold`,
                    description: `Notify when the cost or number of idle ${type} is above a configurable threshold.`,
                    icon: <CircleNumber1 />,
                    behavior,
                });
            }
        }
        return result;
    }

    private createTagHealthAlertPreset(): INotificationPresetItem<any> {
        const xb = this.createExprBuilder<{ ['Health %']: number }>();
        const id = this.createId('tag-health-report');
        const { gridConfig } = this.createTagHealthGrid();
        const label = 'Overall Tag Health Below Threshold';
        const description = 'Notify when the tag health of resources drops below a threshold.';
        const defaultConfig = {
            Title: label,
            Description: description,
            Trigger: {
                ...this.defaultAlertTrigger(),
                Threshold: {
                    PresentationOptions: {
                        settings: [
                            {
                                optionId: 'grid-alert',
                                model: {
                                    filter: [xb.resolve(xb.model['Health %']!.lt(0.6))],
                                },
                            },
                        ],
                    },
                },
            },
            QueryOptions: this.defaultQueryOptions(this.baseQueryDatasource.name),
        };
        const behavior = this.initializeBahavior(id, gridConfig, {}, defaultConfig).form(this.createStandardAlertBehavior(undefined, true));

        return {
            id,
            label,
            description,
            icon: <BorderAll />,
            behavior,
        };
    }

    private createCustomResourceAlertPreset(): INotificationPresetItem<any> {
        const dimExpr = this.baseQueryDatasource.getDefaultGroup();
        const metricExpr = this.baseQueryDatasource.getDefaultValue();

        const chartInput = this.createCustomChartBehavior(false);

        const behavior = this.createBehavior(({ data }) => {
            return this.mergeConfig(
                {
                    ComponentConfig: this.createCustomChartConfig('bar', dimExpr, metricExpr),
                    QueryOptions: this.defaultQueryOptions(this.baseQueryDatasource.name),
                    Trigger: this.defaultAlertTrigger(),
                },
                data
            );
        })
            .addForm(({ data }) => {
                const configChanged = new EventEmitter(data.ComponentConfig!);

                const chartSettings = chartInput(data, configChanged.emit);

                return [
                    chartSettings,
                    this.createSection(
                        'Alert Settings',
                        this.createSectionItem('Title and Description', ...this.createDescriptionInputs(data)),
                        this.createAlertConditionSection(data, configChanged)
                    ),
                ];
            })
            .addValidation(() => []);

        return this.createCustomAlertPreset(behavior);
    }
    // #endregion

    // #region Reports
    private createWeeklyTagHealthReportPreset(): INotificationPresetItem<any> {
        const id = this.createId('tag-health-alert');
        const { gridConfig, inputs } = this.createTagHealthGrid();
        const label = 'Weekly Tag Health Report';
        const description = 'Weekly report on the tag health of your resources.';
        const defaultConfig = {
            Title: 'Weekly Tag Health Report',
            Description: description,
            Trigger: this.defaultSchedTrigger('Weekly'),
            QueryOptions: this.defaultQueryOptions(this.baseQueryDatasource.name),
        };
        const behavior = this.initializeBahavior(id, gridConfig, {}, defaultConfig).standardReport(() => ({
            dataOptions: (data) => {
                const pivotPicker = inputs(data.ComponentConfig.settings);
                return this.createSectionItem('Data Options', pivotPicker);
            },
        }));

        return {
            id,
            label,
            description,
            icon: <BorderAll />,
            behavior,
        };
    }

    private createCustomResourceReportPreset(): INotificationPresetItem<any> {
        const dimExpr = this.baseQueryDatasource.getDefaultGroup();
        const metricExpr = this.baseQueryDatasource.getDefaultValue();

        const chartInput = this.createCustomChartBehavior(false);

        const behavior = this.createBehavior(({ data }) => {
            return this.mergeConfig(
                {
                    ComponentConfig: this.createCustomChartConfig('bar', dimExpr, metricExpr),
                    QueryOptions: this.defaultQueryOptions(this.baseQueryDatasource.name),
                    Trigger: this.defaultSchedTrigger('Weekly'),
                },
                data
            );
        })
            .addForm(({ data }) => {
                const chartSettings = chartInput(data);

                return [
                    chartSettings,
                    this.createSection(
                        'Report Settings',
                        this.createSectionItem('Title and Description', ...this.createDescriptionInputs(data)),
                        this.createScheduleForm(data.Trigger?.Schedule!)
                    ),
                ];
            })
            .addValidation(() => []);

        return this.createCustomReportPreset(behavior);
    }
    // #endregion

    private createTagHealthGrid() {
        const dimExpr = this.baseQueryDatasource.getDefaultGroup();
        const xb = this.createExprBuilder<BaseResource>();
        const scoreCols = this.selectExprs({
            'Coverage %': xb.avg(xb.model.CoverageScore!),
            'Clarity %': xb.avg(xb.model.ClarityScore!),
            'Compliance %': xb.avg(xb.model.ComplianceScore!),
            'Health %': xb.avg(xb.model.TagHealthScore!),
        });

        const gridConfig = this.createGridChartConfig(
            { id: 'pivot', select: dimExpr, type: 'string', formatter: 'string', width: 225, sortDir: 'Asc' },
            ...scoreCols.map((c) => ({ id: c.Alias!, select: c, type: 'number', formatter: 'percent' as NamedFormats, width: 125 }))
        );

        const inputs = (data: GridChartSettings) => {
            const updateAlias = (fld: QueryExpr) => {
                data.columns[0].select!.Alias = this.schemaCtx.queryDescriptorSvc.getName(fld);
            };
            return this.createInput<GridChartSettings>()
                .type('string-expr')
                .options({
                    label: 'View By',
                    schemaCtx: this.schemaCtx,
                    acceptConstant: false,
                    icon: <Eye />,
                    operations: [],
                })
                .map((d) => d.columns[0].select!.Expr! as QueryExpr)
                .bind(data, updateAlias);
        };
        return { gridConfig, inputs };
    }

    private createExprBuilder<TParams>() {
        type Model = BaseResource & TParams;
        const { builder } = exprBuilder<Model>();
        return builder;
    }
}
@injectable()
export class ResourceDatasourcePresetsBuilderFactory implements IDatasourcePresetsBuilderFactory {
    public constructor(
        @inject(ResourceQueryDatasourceFactory) private readonly datasourceFactory: ResourceQueryDatasourceFactory,
        @inject(ResourceQueryService) private readonly resourceQuerySvc: ResourceQueryService,
        @inject(ICompanyContextToken) private readonly company: Company,
        @inject(UserFeatureAccessService) private readonly featureAccessSvc: UserFeatureAccessService,
        @inject(IdleResourcesService) private readonly idleResourcesSvc: IdleResourcesService
    ) {}

    public getSupportedDatasources(): QueryDatasourceMetadata[] {
        return [this.datasourceFactory.getMetadata()];
    }
    public async get(datasourceName: string) {
        const datasource = await this.getDatasourceByName(datasourceName);
        const [types, featureAccessLookup, idleQueries] = await Promise.all([
            datasource.schema.getSchema(),
            this.featureAccessSvc.getAccessLookup(this.company),
            this.idleResourcesSvc.getIdleQuery(),
        ]);
        const schemaSvc = SchemaService.create(types);

        return new ResourceDatasourcePresetsBuilder(datasource, schemaSvc, featureAccessLookup, idleQueries);
    }

    private async getDatasourceByName(name: string) {
        switch (name.toLowerCase()) {
            case 'resources':
                return await this.datasourceFactory.getDatasource((q) => this.resourceQuerySvc.query(q, this.company.Id ?? 0));
            default:
                throw new Error(`Unsupported datasource ${name}`);
        }
    }
}
